import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, ArrowRight } from 'lucide-react';

/**
 * "Become a Community Partner" feature card (Iter 157 redesign — May 2026).
 *
 * Sits in the 3-up row on Discover next to YourAlertsCard and NearbyDealsToggle.
 * Gift icon in circular container, prominent "Earn 50% on every paid boost"
 * tagline, "Learn more" CTA. Entire card is clickable → /affiliate.
 *
 * Note: callers should still gate this on `flags.affiliatesEnabled`.
 */
const CommunityPartnerCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Smart routing: existing partners (helper session present) go straight to
  // their dashboard; everyone else to the registration page. A stale token is
  // handled by HelperDashboard, which redirects to /helper/login if invalid.
  const go = () => {
    let token = null;
    try { token = localStorage.getItem('helper_token'); } catch (e) { token = null; }
    navigate(token ? '/helper/dashboard' : '/affiliate');
  };

  return (
    <button
      type="button"
      onClick={go}
      data-testid="community-partner-card"
      aria-label={t('communityPartner.label', 'Become a Community Partner')}
      className="group relative flex h-full w-full flex-col text-left rounded-2xl border border-purple-300/60 bg-gradient-to-br from-purple-100/70 via-white to-fuchsia-100/60 shadow-sm hover:shadow-lg hover:border-purple-400/70 active:scale-[0.99] transition-all overflow-hidden p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
    >
      {/* decorative confetti dots */}
      <span aria-hidden className="pointer-events-none absolute top-3 right-6 h-1.5 w-1.5 rounded-full bg-amber-400/80" />
      <span aria-hidden className="pointer-events-none absolute top-8 right-12 h-1 w-1 rounded-full bg-fuchsia-400/70" />
      <span aria-hidden className="pointer-events-none absolute top-14 right-3 h-1.5 w-3 rounded-full bg-cyan-400/60 rotate-12" />
      <span aria-hidden className="pointer-events-none absolute -bottom-10 -right-10 h-32 w-48 rounded-full bg-purple-300/20 blur-2xl" />

      <div className="flex items-start gap-4">
        <span className="relative flex-shrink-0 h-14 w-14 rounded-full bg-white border border-purple-100 shadow-inner flex items-center justify-center">
          <Gift className="h-7 w-7 text-purple-600" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-bold text-purple-900 leading-tight">
            {t('communityPartner.title', 'Become a Community Partner')}
          </div>
          <p className="mt-1 text-sm text-purple-900/80 leading-snug">
            {t('communityPartner.tagline', 'Earn ')}
            <span className="font-bold text-purple-700">50%</span>
            {t('communityPartner.taglineRest', ' lifetime commission on everything your referred businesses pay (excl. IVA).')}
          </p>
        </div>
      </div>

      {/* footer CTA pill */}
      <div className="mt-auto pt-4 flex">
        <span
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-purple-600 text-white text-xs font-semibold shadow-sm group-hover:bg-purple-700 transition-colors"
        >
          {t('communityPartner.cta', 'Learn more')}
          <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
        </span>
      </div>
    </button>
  );
};

export default CommunityPartnerCard;
